import { useMutation } from 'react-query'
import { useEnvContext } from 'next-runtime-env'
import { v4 as uuidv4 } from 'uuid'

import { useAPIToken } from '@dc-shared-web/providers/APITokenProvider'
import { useAuth } from '@dc-shared-web/providers/AuthProvider'
import {
  ActivityCategory,
  ActivityItemType,
  ActivitySubCategory,
  ActivityStatus,
  PostActivityRequest,
  PostActivityResponse,
  ActivityData,
} from '@dc-shared-web/hooks/api/activity/types'
import { fetchActivity } from '@dc-shared-web/hooks/api/activity/fetchActivity'

export const useActivityService = () => {
  const { NEXT_PUBLIC_SVC_ACTIVITY_API_URL } = useEnvContext()
  const { token } = useAPIToken()
  const { user } = useAuth()

  const postActivity = async (
    request: PostActivityRequest
  ): Promise<PostActivityResponse> => {
    const activityGuid = request.activityGuid || uuidv4()

    const payload: ActivityData = {
      app_name: 'dcrunch-web',
      actor_guid: user?.uuid || '00000000-0000-0000-0000-000000000000',
      activity_guid: activityGuid,
      item_guid: request.itemGuid,
      item_type: request.itemType,
      category: request.category,
      sub_category: request.subCategory,
      data: {
        user: user?.email || 'anonymous',
        branch: process.env.NODE_ENV || 'development',
        status: request.success ? ActivityStatus.SUCCESS : ActivityStatus.ERROR,
        ...request.data,
      },
    }

    return await fetchActivity(
      NEXT_PUBLIC_SVC_ACTIVITY_API_URL || '',
      token || '',
      payload
    )
  }

  const mutation = useMutation<
    PostActivityResponse,
    Error,
    PostActivityRequest
  >(postActivity, {
    onError: (error) => {
      // Graceful failure - log error but don't break main functionality
      console.warn('Activity tracking failed:', error.message)
    },
  })

  const trackActivity = (
    category: ActivityCategory,
    subCategory: ActivitySubCategory,
    options: {
      itemGuid?: string
      itemType?: ActivityItemType
      success?: boolean
      data?: Record<string, string | number | boolean>
      activityGuid?: string
    } = {}
  ) => {
    return mutation.mutate({
      category,
      subCategory,
      success: options.success ?? true,
      itemGuid: options.itemGuid,
      itemType: options.itemType,
      data: options.data,
      activityGuid: options.activityGuid,
    })
  }

  // Convenience methods for common download tracking
  const trackDownload = (
    itemGuid: string,
    downloadType: 'pdf' | 'checklist' | 'playbook' | 'notes',
    options: {
      crunchLabel?: string
      fileName?: string
      success?: boolean
      data?: Record<string, string | number | boolean>
    } = {}
  ) => {
    return trackActivity(
      ActivityCategory.CRUNCH,
      ActivitySubCategory.DOWNLOADED,
      {
        itemGuid,
        itemType: ActivityItemType.CRUNCH,
        success: options.success ?? true,
        data: {
          download_type: downloadType,
          crunch_label: options.crunchLabel || '',
          file_name: options.fileName || '',
          ...options.data,
        },
      }
    )
  }

  return {
    trackActivity,
    trackDownload,
    isLoading: mutation.isLoading,
    error: mutation.error,
  }
}

export {
  ActivityCategory,
  ActivitySubCategory,
  ActivityItemType,
  ActivityStatus,
} from '@dc-shared-web/hooks/api/activity/types'
